import {
  PageContent,
  PageTitle,
  PageTitle2,
  PageTitle3,
} from "../../components/Page";
import { CampingRoute } from "../../router/CampingRoute";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faTree,
  faCalendarDays,
  faCampground,
  faWater,
  faCoins,
  faPersonSwimming,
  faRestroom,
  faCreditCard,
  faMoneyBill1,
  faWifi,
} from "@fortawesome/free-solid-svg-icons";
import { faCcVisa, faCcMastercard } from "@fortawesome/free-brands-svg-icons";
import { EVENTS } from "./events";
import { EventDate } from "../../components/EventDate";
import { Coordinates } from "../../components/Coordinates";
import { FC, PropsWithChildren } from "react";

const ColumnFlex: FC<PropsWithChildren<{}>> = ({ children }) => (
  <div className="grid gap-6 lg:grid-cols-2">{children}</div>
);

const ColumnFlexGrow: FC<PropsWithChildren<{}>> = ({ children }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-sm backdrop-blur">
    {children}
  </div>
);

const EventContent: FC<PropsWithChildren<{}>> = ({ children }) => (
  <div className="flex-1 space-y-4">{children}</div>
);

const EventDescription: FC<PropsWithChildren<{}>> = ({ children }) => (
  <p className="relative m-0 text-sm leading-relaxed text-slate-600">
    {children}
  </p>
);

const EventSubItemTitle: FC<PropsWithChildren<{}>> = ({ children }) => (
  <h4 className="relative m-0 text-sm font-semibold text-slate-700">
    {children}
  </h4>
);

const EventDetailsList: FC<PropsWithChildren<{}>> = ({ children }) => (
  <dl className="mt-3 grid grid-cols-2 gap-4">{children}</dl>
);

const EventDetailsListTitle: FC<PropsWithChildren<{}>> = ({ children }) => (
  <dt className="min-h-[1.25rem] text-xs font-semibold uppercase tracking-wide text-slate-500">
    {children}
  </dt>
);

const EventDetailsListValue: FC<PropsWithChildren<{}>> = ({ children }) => (
  <dd className="text-sm text-slate-700">{children}</dd>
);

interface EventImagePanelProps {
  src: string;
  side: "left" | "right";
  className?: string;
}

const EventImagePanel: FC<EventImagePanelProps> = ({
  src,
  side,
  className = "",
}) => {
  const overlayClass =
    side === "right"
      ? "left-0 bg-gradient-to-r from-white to-transparent"
      : "right-0 bg-gradient-to-l from-white to-transparent";
  const edgeClass = side === "right" ? "rounded-l-none" : "rounded-r-none";

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${edgeClass} bg-slate-100 shadow-sm ${className}`}
    >
      <img className="h-full w-full object-contain" src={src} alt="Event" />
      <div
        className={`pointer-events-none absolute inset-y-0 w-1/3 md:w-1/4 ${overlayClass}`}
      />
    </div>
  );
};

interface EventDetailsProps {
  hours: string;
  signer?: string;
  cost?: string[];
  admission?: string;
}

function EventDetails({ hours, cost, admission, signer }: EventDetailsProps) {
  const costItems = cost?.filter((item) => item.trim() !== "") ?? [];

  return (
    <>
      <EventDetailsList>
        {signer !== undefined && (
          <div className="flex flex-col gap-1">
            <EventDetailsListTitle>Chansonnier :</EventDetailsListTitle>
            <EventDetailsListValue>{signer}</EventDetailsListValue>
          </div>
        )}
        {costItems.length > 0 && (
          <div className="flex flex-col gap-1">
            <EventDetailsListTitle>Coût :</EventDetailsListTitle>
            <EventDetailsListValue>
              <div className="space-y-1">
                {costItems.map((item) => (
                  <p key={item} className="m-0">
                    {item}
                  </p>
                ))}
              </div>
            </EventDetailsListValue>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <EventDetailsListTitle>Heures :</EventDetailsListTitle>
          <EventDetailsListValue>{hours}</EventDetailsListValue>
        </div>
        {admission !== undefined && (
          <div className="flex flex-col gap-1">
            <EventDetailsListTitle>Admission :</EventDetailsListTitle>
            <EventDetailsListValue>{admission}</EventDetailsListValue>
          </div>
        )}
      </EventDetailsList>
    </>
  );
}

function Informations() {
  return (
    <PageContent>
      <PageTitle className="text-2xl font-semibold text-slate-900 md:hidden">
        Informations
      </PageTitle>
      <ColumnFlex>
        <ColumnFlexGrow>
          <PageTitle2 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
            <FontAwesomeIcon icon={faLocationDot} /> Coordonnées
          </PageTitle2>
          <Coordinates />
        </ColumnFlexGrow>
        <ColumnFlexGrow>
          <PageTitle2 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
            <FontAwesomeIcon icon={faTree} /> Saison 2026
          </PageTitle2>
          <p className="text-sm text-slate-600">15 mai au 20 septembre</p>
        </ColumnFlexGrow>
      </ColumnFlex>
      <PageTitle2 className="mt-10 flex items-center gap-2 text-base font-semibold text-slate-800">
        <FontAwesomeIcon icon={faCalendarDays} /> Évènements
      </PageTitle2>
      <div className="mt-4 space-y-6">
        {EVENTS.map((event) => (
          <div
            key={event.title}
            className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-sm backdrop-blur md:p-6"
          >
            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <div className="shrink-0">
                    <div className="rounded-2xl bg-emerald-50/80 p-3 ring-1 ring-emerald-100">
                      <EventDate date={event.date} />
                    </div>
                  </div>
                  {event.images?.[0] && (
                    <a
                      className="block lg:hidden"
                      href={event.images[0]}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <EventImagePanel
                        className="h-52 sm:h-60"
                        side="right"
                        src={event.images[0]}
                      />
                    </a>
                  )}
                  <EventContent>
                    <PageTitle3 className="text-xs tracking-[0.1em] text-emerald-700">
                      {event.title}
                    </PageTitle3>
                    {event.description && (
                      <EventDescription>{event.description}</EventDescription>
                    )}
                    {event.hours && (
                      <EventDetails
                        cost={event.cost}
                        hours={event.hours}
                        signer={event.signer}
                        admission={event.admission}
                      />
                    )}
                    {event.subitems && (
                      <div className="space-y-3">
                        {event.subitems.map((subItem) => {
                          const subImage = subItem.images?.[0];

                          return (
                            <div
                              key={subItem.title}
                              className="rounded-xl bg-slate-50 p-4"
                            >
                              <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
                                {subImage && (
                                  <a
                                    className="block"
                                    href={subImage}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <EventImagePanel
                                      className="h-44 md:h-auto md:w-44"
                                      side="left"
                                      src={subImage}
                                    />
                                  </a>
                                )}
                                <div className="flex-1 space-y-3">
                                  <EventSubItemTitle>
                                    {subItem.title}
                                  </EventSubItemTitle>
                                  <EventDetails
                                    cost={subItem.cost}
                                    hours={subItem.hours}
                                    admission={subItem.admission}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </EventContent>
                </div>
              </div>
              {event.images?.[0] && (
                <a
                  className="hidden lg:block"
                  href={event.images[0]}
                  target="_blank"
                  rel="noreferrer"
                >
                  <EventImagePanel
                    className="h-52 sm:h-60 lg:h-auto lg:w-64 xl:w-72"
                    side="right"
                    src={event.images[0]}
                  />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <ColumnFlex>
          <ColumnFlexGrow>
            <PageTitle2 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
              <FontAwesomeIcon icon={faCampground} /> Particularités du camping
            </PageTitle2>
            <p className="text-sm text-slate-600">50 emplacements</p>
            <p className="text-sm text-slate-600">
              Petit dépanneur (pain, lait, beurre, glace, bois, ...)
            </p>

            <PageTitle2 className="mb-4 mt-6 flex items-center gap-2 text-base font-semibold text-slate-800">
              <FontAwesomeIcon icon={faPersonSwimming} /> Activités
            </PageTitle2>
            <p className="text-sm text-slate-600">
              Piscine extérieure, pédalo, kayak, chaloupe, pêche, terrain de
              jeux, trampoline
            </p>

            <PageTitle2 className="mb-4 mt-6 flex items-center gap-2 text-base font-semibold text-slate-800">
              <FontAwesomeIcon icon={faRestroom} /> Services
            </PageTitle2>
            <p className="text-sm text-slate-600">
              14 terrains ayant 3 services sont disponibles pour passagers
            </p>
            <p className="text-sm text-slate-600">
              6 terrains pour les tentes ayant 2 services sont disponibles [#45
              à #50]
            </p>
            <p className="text-sm text-slate-600">
              Douches, électricité – 30 ampères, glace/bois laveuse/sécheuse,
              salle communautaire station de vidange, petit dépanneur
            </p>
            <p className="text-sm text-slate-600">
              <FontAwesomeIcon color="gray" icon={faWifi} /> Internet sans fil
            </p>
          </ColumnFlexGrow>
          <ColumnFlexGrow>
            <PageTitle2 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
              <FontAwesomeIcon icon={faWater} /> Services de location
            </PageTitle2>
            <p className="text-sm text-slate-600">Location d’embarcations</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-600">
              <li>Pédalos (1 disponibles)</li>
              <li>Kayak (2 disponibles)</li>
              <li>Chaloupes (2 disponibles)</li>
            </ul>
            <p className="mt-3 text-sm text-slate-600">
              Ceintures de sécurité incluses
            </p>
            <PageTitle2 className="mb-4 mt-6 flex items-center gap-2 text-base font-semibold text-slate-800">
              <FontAwesomeIcon icon={faCoins} /> Modes de paiement
            </PageTitle2>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>
                <FontAwesomeIcon color="gray" icon={faMoneyBill1} /> Comptant
              </li>
              <li>
                <FontAwesomeIcon color="gray" icon={faCreditCard} /> Interac
              </li>
              <li>
                <FontAwesomeIcon color="gray" icon={faCcVisa} /> Visa
              </li>
              <li>
                <FontAwesomeIcon color="gray" icon={faCcMastercard} />{" "}
                Mastercard
              </li>
            </ul>
          </ColumnFlexGrow>
        </ColumnFlex>
      </div>
    </PageContent>
  );
}

export const InformationsRoute: CampingRoute = {
  title: "Informations",
  path: "/informations",
  element: <Informations />,
};
